// Same-game highlight — resolve a positions-page row to the game it belongs to.
//
// ONE RULE: the key comes from HYDRA, and nowhere else. HYDRA_API.md:593 is
// explicit about why — canonicalGameKey is alias-mapped ("Kalshi CHW and
// Polymarket CWS both collapse to canonical CHW"), and any consumer grouping
// Kalshi + Polymarket for the same game "should key on this field rather than
// parsing the raw ticker — raw tickers use different codes across venues".
//
// An earlier pass re-derived the key from the ticker string as a fallback (as
// ValueDashboard:positionGameKeys still does). An adversarial review executed it
// against the shapes in the parser's own parse.test.ts and it was wrong on most
// of the league:
//   - parseNflGame truncates a 3-char home code (nfl.ts:142, `slice(3, 5)` with
//     a dead `||` branch): DAL@PHI -> home "PH". Never matches anything.
//   - parseNcaaGameGeneric splits 3/3 on a 4-char school (ncaa.ts:51):
//     "UCLAORE" -> away "UCL", home "AOR".
//   - KXNCAAMBGAME and KXNCAAWBGAME both map to sport `cbb` and parse
//     identically, so the MEN'S and WOMEN'S Duke-UNC collapse onto ONE key — a
//     row that highlights the WRONG GAME, which is worse than not highlighting.
//   - NHL 2-char codes (TB), MLB F5 TIE rows, and every tennis ticker parse to
//     no game at all.
// Those are pre-existing parser bugs, not ours, and fixing them means editing
// shared code four other features depend on. So the fallback is gone. A Kalshi
// row whose market hydra has already pruned simply doesn't highlight.
//
// WHAT THAT COSTS: a settled Kalshi game's rows stop grouping once hydra ages
// the frame out. Live rows — every market you can still trade — are unaffected,
// and settled POLYMARKET rows still group, because their key was persisted while
// the market was live (describeOrder's two caches). Sports hydra ships no key
// for at all (NHL, NBA, boxing today) never highlight on either venue.

import { describePolyMarketCached, describePolyOrderCached } from '@/lib/polymarket/describeOrder';
import { promoteToGameKeyV2 } from '@/services/analyticsGameKey';
import { useMarketStore } from '@/stores/marketStore';

/** `sport|date|gameNo|TEAM_A|TEAM_B` — exactly five segments. Content is NOT
 *  constrained: hydra's tennis keys carry full surnames, which are mixed-case
 *  and may be hyphenated ("tennis|2026-07-03|1|Auger-Aliassime|Sinner"). */
function isGameKeyShape(key: string): boolean {
  return key.split('|').length === 5;
}

/**
 * Canonicalize a key so two rows of one game compare equal by string equality:
 * promoteToGameKeyV2 upper-cases and alpha-sorts the two team codes, so it
 * doesn't matter which order hydra happened to emit them in on each venue.
 *
 * Anything that isn't a 5-seg game key is REJECTED rather than passed through.
 * promoteToGameKeyV2 returns its input unchanged when it can't parse it, so a
 * playoff-series key (`nba|series|26|R1|PHI|BOS`, six segments — marketStore.ts
 * builds these) would otherwise flow straight out of here as a "game key".
 */
function normalize(key: string | null | undefined): string | null {
  if (!key) return null;
  const promoted = promoteToGameKeyV2(key);
  return isGameKeyShape(promoted) ? promoted : null;
}

/**
 * Hydra's key straight off the live market frame. Null when the market isn't in
 * the store — never loaded (sport tab unopened) or pruned (settled).
 *
 * v2 ONLY. The legacy 4-seg `canonicalGameKey` carries no game number, so
 * promoting it silently assumes game 1 — which makes a doubleheader's GAME 2
 * compare equal to GAME 1 and light up the wrong game's rows. Costs nothing to
 * refuse: prod hydra ships ZERO markets with a legacy key but no v2 (sampled
 * 1717 across 11 sports).
 */
function keyFromMarket(ticker: string | null | undefined): string | null {
  if (!ticker) return null;
  return normalize(useMarketStore.getState().getMarket(ticker)?.canonicalGameKeyV2);
}

/**
 * The canonical same-game key for a positions-page row, or null when the row
 * can't be resolved to a game (event tickers, series rows, sports hydra has no
 * canonical key for, markets whose sport tab hasn't loaded, settled Kalshi
 * rows). A null key never highlights.
 *
 * Live store read FIRST, persisted caches only as the pruned-market fallback: a
 * resolved description never re-resolves, so a stale cached key must never get
 * the chance to poison a market that is still live and still telling us the
 * truth.
 */
export function rowGameKey(vm: { venue: 'kalshi' | 'polymarket' | 'polymarket_us'; ticker: string }): string | null {
  const store = useMarketStore.getState();

  // 0. Polymarket US rows carry the venue's market slug; the store indexes the
  //    PMUS- frame by that slug while the market lives. No persisted fallback
  //    yet (F4) — pruned means gone, like Kalshi.
  if (vm.venue === 'polymarket_us') {
    const us = store.getMarketByPmusSlug(vm.ticker);
    return us ? keyFromMarket(us.ticker) : null;
  }

  // 1. The ticker keys marketStore directly — every Kalshi ticker, and any PM-
  //    slug ticker.
  const direct = keyFromMarket(vm.ticker);
  if (direct) return direct;

  // 2. Kalshi has no persisted fallback (see the header): pruned means gone.
  if (vm.venue === 'kalshi') return null;

  // 3. A poly POSITION carries a raw 0x conditionId; a poly ORDER carries the
  //    CLOB asset id. Both index back to the slug ticker while the market lives.
  const indexed = keyFromMarket(
    store.getTickerByConditionId(vm.ticker) ?? store.getTickerByTokenId(vm.ticker),
  );
  if (indexed) return indexed;

  // 4. Market pruned. TWO persisted caches, keyed differently — dispatch on the
  //    id shape and never cross them:
  //      - a POSITION's 0x conditionId -> the MARKET-level cache. O(1) and
  //        sweep-free; asking the token cache about a conditionId is a
  //        guaranteed miss that also buys a full 14-sport frame sweep, the exact
  //        spike that cache exists to prevent.
  //      - an ORDER's CLOB asset id (a decimal string) -> the token-keyed cache.
  //    Both captured hydra's key while the market was still live, which is the
  //    only moment it exists for these rows.
  if (vm.ticker.toLowerCase().startsWith('0x')) {
    return normalize(describePolyMarketCached(vm.ticker)?.canonicalGameKey);
  }
  return normalize(describePolyOrderCached(vm.ticker)?.canonicalGameKey);
}

/**
 * The highlight rule: a row lights up only when its game key RESOLVED and equals
 * the hovered row's. A null key never highlights — not even against another null
 * (two unresolvable rows are not "the same game").
 */
export function isSameGameHighlighted(
  rowKey: string | null,
  activeGameKey: string | null,
): boolean {
  return rowKey != null && rowKey === activeGameKey;
}

/**
 * Is the hovered row still on the page? The cleanup effect calls this to decide
 * whether to drop a hover that lost its mouse-leave — a row that fills, closes,
 * or is filtered out UNMOUNTS with no leave event, so the highlight would stay
 * stuck lit without this.
 *
 * Keyed by the row's UNIQUE key, NEVER venue+ticker. A position and an order on
 * one ticker are different rows that share a venue+ticker; a game-key match would
 * treat the surviving sibling as proof the hovered row is still alive and leave
 * the whole game stuck highlighted after the hovered row is gone.
 */
export function isHoveredRowPresent(
  hoveredKey: string,
  liveRowKeys: readonly (string | null | undefined)[],
): boolean {
  return liveRowKeys.includes(hoveredKey);
}
